import React from 'react';
import { createRoot } from 'react-dom/client';
import { HashRouter, Navigate, Route, Routes } from "react-router-dom";
import './index.css';
import { QueryClientProvider } from 'react-query';
import { environment } from './api/environment';
import { client, queryClient } from './api/graphql';
import { ThemeProvider } from '@mui/material/styles';
import { planapusTheme } from './theme/planapusTheme';
import CssBaseline from '@mui/material/CssBaseline';
import { AppContext } from './components/AppContext';
import ListProfiles from './components/Link/ListProfiles';
import ProfileLayoutResponsive from './components/Layout/ProfileLayoutResponsive';
import OneProfileFeed from './components/Link/OneProfileFeed';
import OneProfileHistory from './components/Link/OneProfileHistory';
import ProfileHome from './components/Link/ProfileHome';
import PendingSuggestionsPage from './components/suggestions/PendingSuggestionsPage';
import ActivityPage from './components/suggestions/Activity/ActivityPage';
import ProfileSetup from './components/Link/ProfileSetup';
import OneVideo from './components/Link/OneVideo';
import FeedbackView from './components/Link/FeedbackView';
import ListAuthors from './components/Link/ListAuthors';
import Layout from './components/Layout/Layout';
import OneAuthor from './components/Link/OneAuthor';
import AdminVideos from './components/Admin/AdminVideos';
import AdminAgentScores from './components/Admin/AdminAgentScores';
import AgentTuning from './components/Admin/AgentTuning';
import AdminTasks from './components/Admin/AdminTasks';
import AdminCommandCenter from './components/Admin/AdminCommandCenter';
import AdminProfileInsights from './components/Admin/AdminProfileInsights';
import CleanupStrategyDocs from './components/Admin/CleanupStrategyDocs';
import AdminAgentsLab from './components/Admin/AdminAgentsLab';
import AdminCosts from './components/Admin/AdminCosts';
import AdminFirestoreCosts from './components/Admin/AdminFirestoreCosts';
import AdminWaitlist from './components/Admin/AdminWaitlist';
import AdminServiceHealth from './components/Admin/AdminServiceHealth';
import AdminChannelFeedback from './components/Admin/AdminChannelFeedback';
import LinkDesktopSession from './components/Link/LinkDesktopSession';
import GuardianGroups from './components/Link/GuardianGroups';
import AuthPage from './components/Auth/AuthPage';
import PairDevicePage from './components/Auth/PairDevicePage';
import InvitePage from './components/Auth/InvitePage';
import NamePrompt from './components/Auth/NamePrompt';
import {RequireAuth} from './components/Auth/RequireAuth';
import LandingPage from './components/Landing/LandingPage';
import SetupGuide from './components/Setup/SetupGuide';
import Welcome from './components/Welcome/Welcome';
import GetMobile from './components/Get/GetMobile';
import DownloadsPage from './components/Downloads/DownloadsPage';
import {DialogProvider} from './components/ui/ConfirmDialog';
import {useParams} from 'react-router-dom';

// Tiny param-preserving redirect: old `/profile/:pid/author/:aid`
// lands here and bounces to the canonical `/profile/:pid/channel/:cid`.
// Plain <Navigate to="…"> can't template URL params, so we do it manually.
function ChannelRedirect() {
  const {pid, aid} = useParams();
  return <Navigate to={`/profile/${pid}/channel/${aid}`} replace />;
}

// Legacy `/profile/admin/video/:vid` → canonical
// `/admin/content/video/:vid`.  The 'admin' pid sentinel was removed
// when we unified admin-context rendering onto the /admin/... routes;
// this keeps old bookmarks working instead of falling through to the
// profile layout.
function LegacyAdminVideoRedirect() {
  const {vid} = useParams();
  return <Navigate to={`/admin/content/video/${vid}`} replace />;
}

// Legacy `/profile/:pid/actions` → `/profile/:pid/activity/youtube`.
// The old ProfileActionsHistory page conflated suggestions, votes,
// and executed actions; the redesign splits these into /review (the
// suggestions inbox) and /activity (history with three tabs). The
// "actions taken" tab is the closest analog to the old page.
function ProfileActionsRedirect() {
  const {pid} = useParams();
  return <Navigate to={`/profile/${pid}/activity/youtube`} replace />;
}

const root = createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <QueryClientProvider client={queryClient}>
    <React.StrictMode>
      <ThemeProvider theme={planapusTheme}>
        <CssBaseline />
        <AppContext.Provider value={{ environment }}>
        <DialogProvider>
        {/* <Provider store={store}> */}
            {/* https://www.robinwieruch.de/react-router-nested-routes/ */}
          {/* <ScopedCssBaseline> */}
            <HashRouter>
              <Routes>
                {/* /get — install path for the GoodShowHQ mobile app.
                    Public, rendered OUTSIDE the Layout wrapper since
                    it carries its own header/footer and lives at a
                    different theme density than the dashboard.
                    Declared before sibling routes (and before the
                    duplicate `path="/"` further down) so v7 route
                    ranking has no opportunity to shadow it. */}
                <Route path="/get" element={<GetMobile />} />
                {/* Public downloads — desktop (mac/win/linux) + the
                    Home Assistant add-on. Same reasoning as /get:
                    own chrome, own theme, outside Layout. */}
                <Route path="/downloads" element={<DownloadsPage />} />
                {/* Public landing page — rendered OUTSIDE the
                    Layout wrapper because the marketing surface
                    has its own header/footer and its own theme.
                    Putting it inside Layout would double-render the
                    nav. */}
                <Route path="/" element={<LandingPage />} />
                {/* AuthPage renders OUTSIDE the Layout wrapper —
                    it carries its own logo, theme, and full-page
                    background. Putting it inside Layout would
                    double-render the app bar. */}
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/pair" element={<PairDevicePage />} />
                {/* Magic-link invite landing (desktop + co-parent).
                    Public — auto-logs the recipient in via a custom token
                    minted from the invite, then links the profiles. */}
                <Route path="/invite" element={<InvitePage />} />
                {/* Public parent-education guide. Lives outside the
                    Layout wrapper for the same reason as the landing
                    page — it carries its own header / footer / theme
                    treatment. Reachable from the landing FAQ, the
                    profile-selector empty state, and the welcome
                    flow. */}
                <Route path="/setup" element={<SetupGuide />} />
                {/* First-run checklist for new accounts. Auth-gated
                    via RequireAuth — unauthenticated visits bounce
                    to /auth. Lives outside Layout for the same
                    reason as /auth and /setup: it carries its own
                    minimal header and slate-50 background. */}
                <Route
                  path="/welcome"
                  element={
                    <RequireAuth>
                      <Welcome client={client} />
                    </RequireAuth>
                  }
                />
                {/* Mandatory display-name entry for phone-auth accounts.
                    Auth-gated; `decidePostAuthRoute` routes fresh parents
                    here (with a ?next= carrying their intended destination)
                    before letting them into the app. Lives outside Layout
                    for the same reason as /auth and /welcome. */}
                <Route
                  path="/name"
                  element={
                    <RequireAuth>
                      <NamePrompt />
                    </RequireAuth>
                  }
                />
                <Route path="/" element={<Layout client={client}/>}>
                  {/* `/` is now the landing page. The app routes
                      below are reached via `/profile`, `/admin/...`,
                      etc. — Layout's index redirect is removed. */}
                  <Route path="/link" element={<LinkDesktopSession client={client} />} />
                  {/* Guardian groups — co-parenting circles. Authed +
                      inside the Layout chrome, alongside /profile. */}
                  <Route
                    path="/groups"
                    element={<RequireAuth><GuardianGroups client={client} /></RequireAuth>}
                  />
                  <Route
                    path="/profile"
                    element={<RequireAuth><ListProfiles client={client} /></RequireAuth>}
                  />
                  {/* Plural alias — long-standing footgun: users
                      bookmark or type `/profiles` (the natural URL
                      for a list) and the singular-only route returned
                      a 404 / fell back to the hash-only landing
                      (`profiles/#profiles`). Render the same list
                      under both. */}
                  <Route
                    path="/profiles"
                    element={<RequireAuth><ListProfiles client={client} /></RequireAuth>}
                  />
                  {/* Legacy admin-video URL. Static `admin` segment beats
                      the sibling `/profile/:pid/video/:vid` dynamic match
                      in React Router v6, so this catches old bookmarks
                      and redirects out of the profile layout entirely
                      to the canonical /admin/content/video/:vid. */}
                  <Route path="/profile/admin/video/:vid" element={<LegacyAdminVideoRedirect />} />
                  <Route
                    path="/profile/:pid"
                    element={<RequireAuth><ProfileLayoutResponsive client={client}/></RequireAuth>}
                  >
                    {/* The single-page home replaces the old 5-tab nav.
                        Report and Settings are folded into this page —
                        the verdict + sparkline at the top replaces
                        the Report tab, and the corrective-actions
                        inbox ("For your review") replaces Settings. */}
                    <Route index element={<ProfileHome client={client} />} />
                    <Route path="/profile/:pid/setup" element={<ProfileSetup client={client} />} />
                    <Route path="/profile/:pid/review" element={<PendingSuggestionsPage client={client} />} />
                    <Route path="/profile/:pid/review/pending" element={<PendingSuggestionsPage client={client} />} />
                    {/* Activity surface (PR 4) — three tabs as nested URL
                        segments so deep-links land on the right tab. The
                        index path redirects to /youtube (the default tab). */}
                    <Route path="/profile/:pid/activity" element={<Navigate to="youtube" replace />} />
                    <Route path="/profile/:pid/activity/youtube" element={<ActivityPage client={client} />} />
                    <Route path="/profile/:pid/activity/decisions" element={<ActivityPage client={client} />} />
                    <Route path="/profile/:pid/activity/pending" element={<ActivityPage client={client} />} />
                    {/* The old /actions page conflated suggestions, votes,
                        and executed actions; the redesign splits those into
                        /review and /activity/* respectively. /actions
                        permanently redirects to /activity/youtube — the
                        closest analog. */}
                    <Route path="/profile/:pid/actions" element={<ProfileActionsRedirect />} />
                    <Route path="/profile/:pid/feedback" element={<FeedbackView client={client} />} />
                    <Route path="/profile/:pid/feed" element={<OneProfileFeed client={client} />} />
                    <Route path="/profile/:pid/history" element={<OneProfileHistory client={client} />} />
                    <Route path="/profile/:pid/video/:vid" element={<OneVideo client={client} />} />
                    <Route path="/profile/:pid/channels" element={<ListAuthors client={client} />} />
                    <Route path="/profile/:pid/channel/:aid" element={<OneAuthor client={client} />} />
                    {/* Back-compat redirects — old bookmarks / deep links
                        land here and bounce to the canonical IA. The
                        former Report and Settings routes both fold
                        back into the home; their content is rendered
                        inline there. */}
                    <Route path="/profile/:pid/report" element={<Navigate to=".." replace />} />
                    <Route path="/profile/:pid/settings" element={<Navigate to=".." replace />} />
                    <Route path="/profile/:pid/corrective-actions" element={<Navigate to=".." replace />} />
                    <Route path="/profile/:pid/authors" element={<Navigate to="../channels" replace />} />
                    <Route path="/profile/:pid/author/:aid" element={<ChannelRedirect />} />
                  </Route>
                  {/* Admin Routes — organized into 5 sections:
                      Operations · AI · Content · Costs · Docs.  Old
                      flat URLs (/admin/command-center, /admin/agents,
                      /admin/videos, /admin/costs, …) were removed
                      entirely — no redirects kept. */}

                  {/* Operations — what's running, what's broken, what
                      ran. */}
                  <Route path="/admin/operations/profiles" element={<AdminCommandCenter client={client} />} />
                  <Route path="/admin/operations/profiles/:profileId/insights" element={<AdminProfileInsights client={client} />} />
                  <Route path="/admin/operations/health" element={<AdminServiceHealth client={client} />} />
                  <Route path="/admin/operations/tasks" element={<AdminTasks client={client} />} />
                  <Route path="/admin/operations/tasks/:tab" element={<AdminTasks client={client} />} />
                  <Route path="/admin/operations/waitlist" element={<AdminWaitlist client={client} />} />

                  {/* AI — agents, scores, prompt tuning. */}
                  <Route path="/admin/ai/agents" element={<AdminAgentsLab client={client} />} />
                  <Route path="/admin/ai/scores" element={<AdminAgentScores client={client} />} />
                  <Route path="/admin/ai/agent/:aid/tune" element={<AgentTuning client={client} />} />

                  {/* Content — the videos under the lens.  Admin-side
                      video detail page reuses the OneVideo component;
                      when there is no `pid` in the URL, profile-scoped
                      features (feedback, channel link) degrade to
                      read-only. */}
                  <Route path="/admin/content/videos" element={<AdminVideos client={client} />} />
                  <Route path="/admin/content/video/:vid" element={<OneVideo client={client} />} />
                  <Route path="/admin/content/channel-feedback" element={<AdminChannelFeedback client={client} />} />

                  {/* Costs — LLM dollars + Firestore reads. */}
                  <Route path="/admin/costs/llm" element={<AdminCosts client={client} />} />
                  <Route path="/admin/costs/firestore" element={<AdminFirestoreCosts client={client} />} />

                  {/* Docs. */}
                  <Route path="/admin/docs/cleanup-strategy" element={<CleanupStrategyDocs />} />
                </Route>
              </Routes>
            </HashRouter>
          {/* </ScopedCssBaseline> */}
        {/* </Provider> */}
        </DialogProvider>
        </AppContext.Provider>
      </ThemeProvider>
    </React.StrictMode>
  </QueryClientProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
